import { Capacitor } from '@capacitor/core';
import { CONFIG } from '@/app/config/env';

// On native (Capacitor) the app loads from a local WebView (`https://localhost`),
// so there is no tenant subdomain in the URL to parse. The active tenant instead
// comes from the workspace the user signs in to — seeded from secure storage at
// boot (auth store) and set by the login form. Web builds ignore this entirely.
let nativeSubdomain: string | null = null;

/** Set the active tenant subdomain for native builds. Normalises to a bare,
 *  lower-case label (e.g. "Acme " → "acme"); empty clears it. */
export function setNativeSubdomain(sub: string | null | undefined): void {
  const clean = (sub ?? '').trim().toLowerCase();
  nativeSubdomain = clean.length > 0 ? clean : null;
}

/** The active native tenant subdomain, or null. */
export function nativeSubdomainValue(): string | null {
  return nativeSubdomain;
}

/** Extract the tenant subdomain from the current URL. */
export function currentSubdomain(): string | null {
  if (typeof window === 'undefined') return null;
  const host = window.location.hostname;
  const suffix = '.' + CONFIG.FRONTEND_HOST;
  if (!host.endsWith(suffix)) return null;
  const sub = host.slice(0, -suffix.length);
  return sub.length > 0 && !sub.includes('.') ? sub : null;
}

/** The effective tenant subdomain — hostname first, then dev/native fallback.
 *  In `VITE_DEV_ROOT` mode we want the app to behave like the bare root
 *  domain, so ignore the DEV_TENANT fallback there. */
export function effectiveSubdomain(): string | null {
  // Native: no hostname to parse — use the workspace the user signed in to.
  if (Capacitor.isNativePlatform()) return nativeSubdomain;
  const fromHost = currentSubdomain();
  if (fromHost) return fromHost;
  if (isRootDomain()) return null;
  return CONFIG.DEV_TENANT ?? null;
}

/** True when the current URL is the bare root domain (no subdomain).
 *  Root domain hosts the super-admin console under /admin/**. */
export function isRootDomain(): boolean {
  // The native app is always a tenant client, never the super-admin root console.
  if (Capacitor.isNativePlatform()) return false;
  if (typeof window === 'undefined') return false;
  if (window.location.hostname === CONFIG.FRONTEND_HOST) return true;
  // Local dev override so `npm run dev` can exercise the /admin/* console
  // without wildcard-resolvable DNS.
  if (CONFIG.DEV_ROOT && isLocalHost(window.location.hostname)) return true;
  return false;
}

function isLocalHost(host: string): boolean {
  return host === 'localhost' || host === '127.0.0.1' || host === '::1' || host === '0.0.0.0';
}

/** Protocol to use when building API URLs.
 *  Match the current page's protocol so a production `https://…`
 *  origin never fires `http://…` API requests (browsers block mixed
 *  content, and even when they don't it's a real leak). Local dev
 *  served over `http://localhost` still gets `http://` cleanly. */
function apiProtocol(): string {
  // Native always talks HTTPS to the tenant API, regardless of the WebView scheme.
  if (Capacitor.isNativePlatform()) return 'https:';
  if (typeof window === 'undefined' || !window.location?.protocol) return 'https:';
  const proto = window.location.protocol;
  return proto === 'http:' || proto === 'https:' ? proto : 'https:';
}

/** Build the API base URL for the current tenant. */
export function apiBaseUrl(): string {
  const sub = effectiveSubdomain();
  if (sub) return `${apiProtocol()}//${sub}.${CONFIG.API_HOST}/api`;
  return centralApiBaseUrl();
}

/** Build the API base URL for the central/super-admin API. Always
 *  hits the bare API host, regardless of the browser's current URL.
 *  The backend serves super-admin routes at `/api/*` (no `/central`
 *  prefix) and disambiguates via the `guard: "admin"` field on login. */
export function centralApiBaseUrl(): string {
  return `${apiProtocol()}//${CONFIG.API_HOST}/api`;
}
